import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';

import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import useAuditLogPages from './useAuditLogPages';
import AuditLogsMore from './AuditLogsMore';

// graphy keeps `metadata` (a JSON column) as its JSON text, written by
// Rails' to_json, which turns < > & into \u003c \u003e \u0026. So a reject
// note showed as {"admin_rejection_note":"a \u003cb\u003e..."} (BH-66).
// JSON.parse undoes both; the result is rendered as React text, so a tag in
// a note still shows as text and never runs. Anything that is not a JSON
// object (status, dates, NULL) is shown as graphy sent it.
const METADATA_LABELS: Record<string, string> = {
  admin_rejection_note: 'Rejection note',
};

export const formatAuditValue = (
  columnName: string,
  value: string | null,
): string => {
  if (!value) return 'null';
  if (columnName !== 'metadata') return value;
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return value;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
    return value;
  const entries = Object.entries(parsed as Record<string, unknown>);
  if (entries.length === 0) return 'Empty';
  return entries
    .map(([key, v]) => {
      const label = METADATA_LABELS[key] ?? key.replace(/_/g, ' ');
      const text = typeof v === 'string' ? v : JSON.stringify(v);
      return `${label}: ${text}`;
    })
    .join('; ');
};

// BH-74: a Chip label is one line that never wraps, so a 1,300-character
// note made the row ~8,800px wide and pushed Changed By out of view.
export const AUDIT_CHIP_SX = {
  height: 'auto',
  maxWidth: '100%',
  '& .MuiChip-label': {
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    paddingTop: '2px',
    paddingBottom: '2px',
  },
};

interface AuditLogsModalProps {
  open: boolean;
  workerId: number;
  onClose: () => void;
}

const AuditLogsModal = ({ open, workerId, onClose }: AuditLogsModalProps) => {
  const {
    logs,
    loading,
    loadingMore,
    error,
    moreError,
    workerName,
    total,
    hasMore,
    loadMore,
  } = useAuditLogPages(open, workerId);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose}>
      <DialogTitle>
        Onboarding Steps Audit Logs
        {workerName && (
          <Small color="lighter" style={{ display: 'block', marginTop: '4px' }}>
            {workerName}
          </Small>
        )}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Stack style={{ justifyContent: 'center', padding: '32px' }}>
            <CircularProgress />
          </Stack>
        ) : error ? (
          <Body color="lighter">{error}</Body>
        ) : logs.length === 0 ? (
          <Body color="lighter">No audit logs found</Body>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Step</TableCell>
                  <TableCell>Change</TableCell>
                  <TableCell>Changed By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <Small>{formatDate(log.changed_at)}</Small>
                    </TableCell>
                    <TableCell>
                      <Body weight="semibold">{log.step_title}</Body>
                      <Small color="lighter">{log.column_name}</Small>
                    </TableCell>
                    <TableCell data-testid={`auditChange-${log.id}`}>
                      <Stack wrap gap={8} style={{ alignItems: 'center' }}>
                        <Chip
                          label={formatAuditValue(
                            log.column_name,
                            log.old_value,
                          )}
                          size="small"
                          variant="outlined"
                          sx={AUDIT_CHIP_SX}
                        />
                        <Small color="lighter">→</Small>
                        <Chip
                          color="primary"
                          label={formatAuditValue(
                            log.column_name,
                            log.new_value,
                          )}
                          size="small"
                          sx={AUDIT_CHIP_SX}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Body>{log.changed_by.name}</Body>
                      <Small color="lighter">{log.changed_by.type}</Small>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {!loading && !error && (
          <AuditLogsMore
            error={moreError}
            hasMore={hasMore}
            loadingMore={loadingMore}
            onLoadMore={loadMore}
            shown={logs.length}
            total={total}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuditLogsModal;
